import React from 'react';
import { Route, Routes } from 'react-router-dom';

import ImportTrucks from '@src/flc/components/logistics/import/trucks';
import ImportTruck from '@flc/components/logistics/import/truck';
import MergeImportTrucks from '@flc/components/logistics/import/merge-trucks';
import ImportConsignments from '@flc/components/logistics/import/consignments/consignments';
import ImportConsignment from '@flc/components/logistics/import/consignments/consignment';
import Preloading from '@flc/components/logistics/import/preloading/preloading';
import ConsignmentPreloading from '@src/flc/components/logistics/import/consignments/consignment-preloading';
import ImportBills from '@flc/components/logistics/import/bills/bills';
import DeliveryTrucks from '@flc/components/logistics/delivery/trucks';
import DeliveryTruck from '@flc/components/logistics/delivery/truck';
import Remainings from '@flc/components/warehouse/remainings/remainings';
import Remaining from '@flc/components/warehouse/remainings/remaining';
import ExtraConsignments from '@flc/components/warehouse/extra-consignments/extra-consignments';
import ExtraConsignment from '@flc/components/warehouse/extra-consignments/extra-consignment';
import Cities from '@flc/components/cities/cities';
import City from '@flc/components/cities/city';
import Tags from '@flc/components/tags/tags';
import Tag from '@flc/components/tags/tag';
import Warehouses from '@flc/components/warehouses/warehouses';
import Warehouse from '@flc/components/warehouses/warehouse';
import Consignees from '@flc/components/consignees/consignees';
import Consignee from '@flc/components/consignees/consignee';
import PackageTemplates from '@flc/components/package-templates/package-templates';
import PackageTemplate from '@flc/components/package-templates/package-template';
import TradingPlatforms from '@flc/components/trading-platforms/trading-platforms';
import TradingPlatform from '@flc/components/trading-platforms/trading-platform';
import TransportTariffs from '@flc/components/transport-tariffs/transport-tariffs';
import TransportTariff from '@flc/components/transport-tariffs/transport-tariff';
import Tariffs from '@flc/components/tariffs/tariffs';
import Tariff from '@flc/components/tariffs/tariff';
import BillHeaders from '@flc/components/bills/bill-headers';
import Users from '@flc/components/users/users';
import User from '@flc/components/users/user';
import Contacts from '@flc/components/contacts/contacts';
import Contact from '@flc/components/contacts/contact';
import Orders from '@flc/components/orders/orders';
import Order from '@flc/components/orders/order';
import Feedbacks from '@flc/components/feedbacks/feedbacks';
import Feedback from '@flc/components/feedbacks/feedback';
import Documents from '@flc/components/documents/documents';
import Accounts from '@flc/components/accounts/accounts';
import Account from '@flc/components/accounts/account';
import Debts from '@flc/components/debts/debts';
import Debt from '@flc/components/debts/debt';
import Counterparties from '@flc/components/counterparties/counterparties';
import UserAccount from '@flc/components/user-account/user-account';
import AuditLogs from '@flc/components/audit-logs/audit-logs';
import Balances from '@flc/components/balances/balances';
import BalanceDetails from '@flc/components/balances/balance-details';

import Waybills from '@flc/components/waybills/waybills';
import Waybill from '@flc/components/waybills/waybill';
import CargoCarriers from '@flc/components/cargo-carriers/cargo-carriers';
import CargoCarrier from '@flc/components/cargo-carriers/cargo-carrier';
import Items from '@flc/components/items/items';
import Item from '@flc/components/items/item';
import Countries from '@flc/components/countries/countries';
import Country from '@flc/components/countries/country';

import Waypoints from '@components/waypoints/waypoints';
import Companies from '@components/companies/companies';
import Company from '@components/companies/company';
import Performers from '@components/performers/performers';
import NewNotification from '@components/notifications/new-notification';
import Emails from '@components/notifications/emails';
import PushMessages from '@components/notifications/push-mesages';
import TelegramMessages from '@components/notifications/telegram-messages';
import MaxMessages from '@components/notifications/max-messages';
import SmsMessages from '@components/notifications/sms-messages';
import Acceptance from '@components/acceptance/acceptance';
import WarehouseTruck from '@components/acceptance/warehouse-truck';
import WarehouseConsignment from '@components/acceptance/warehouse-consignment';
import ShipmentJournals from '@components/shipment-journals/shipment-journals';
import ShipmentJournal from '@components/shipment-journals/shipment-journal';
import ManageRoutes from '@components/route-sheet/manage-routes';
import RouteSheet from '@components/route-sheet/route-sheet';

import ClientsActivityReport from '@src/core/components/reports/clients-activity-report';
import UsersReport from '@src/core/components/reports/users-report';
import ImportTrucksReport from '@src/core/components/reports/import-trucks-report';

import { ProtectedRoute } from '@controls/protected-route';
import RedirectRoute from '@controls/redirect-route';

import MainLayout from '@flc/main-layout';

import { IUserSession } from '@entities/user-session';

import { Permission, hasPermission } from '@enums/permission';

import { serverFetch } from '@flc/server';

interface IAppRoutes {
    userSession: IUserSession;
    appVersion: string | undefined;
}

const AppRoutes = (props: IAppRoutes) => {
    const { userSession, appVersion } = props;

    return (
        <Routes>
            <Route path='/' element={<MainLayout appVersion={appVersion} />}>
                <Route index element={<RedirectRoute replace to={`/flc/logistics/import`} />} />
                <Route path='flc' element={<RedirectRoute replace to={`/flc/logistics/import`} />} />

                <Route path='notifications/newnotification' element={<RedirectRoute replace to={`/flc/notifications/newnotification`} />} />
                <Route path='notifications/sms' element={<RedirectRoute replace to={`/flc/notifications/sms`} />} />
                <Route path='notifications/emails' element={<RedirectRoute replace to={`/flc/notifications/emails`} />} />
                <Route path='notifications/push' element={<RedirectRoute replace to={`/flc/notifications/push`} />} />

                {/* TODO: remove redirect section */}

                <Route path='logistics/import' element={<RedirectRoute replace to={`/flc/logistics/import`} />} />
                <Route path='logistics/import/new' element={<RedirectRoute replace to={`/flc/logistics/import/new`} />} />
                <Route path='logistics/import/:id' element={<RedirectRoute replace to={`/flc/logistics/import/:id`} />} />
                <Route path='logistics/import/merge/:ids' element={<RedirectRoute replace to={`/flc/logistics/import/merge/:ids`} />} />
                <Route
                    path='logistics/import/consignments/:truckId'
                    element={<RedirectRoute replace to={`/flc/logistics/import/consignments/:truckId`} />}
                />
                <Route
                    path='logistics/import/consignments/new/:truckId'
                    element={<RedirectRoute replace to={`/flc/logistics/import/consignments/new/:truckId`} />}
                />
                <Route
                    path='logistics/import/consignments/:truckId/:consignmentId'
                    element={<RedirectRoute replace to={`/flc/logistics/import/consignments/:truckId/:consignmentId`} />}
                />
                <Route
                    path='logistics/import/tariffs/:truckId'
                    element={<RedirectRoute replace to={`/flc/logistics/import/tariffs/:truckId`} />}
                />
                <Route path='logistics/delivery' element={<RedirectRoute replace to={`/flc/logistics/delivery`} />} />
                <Route
                    path='logistics/delivery/new/:journalId'
                    element={<RedirectRoute replace to={`/flc/logistics/delivery/new/:journalId`} />}
                />
                <Route
                    path='logistics/delivery/:truckId/:journalId?'
                    element={<RedirectRoute replace to={`/flc/logistics/delivery/:truckId/:journalId?`} />}
                />
                <Route path='warehouse/acceptance' element={<RedirectRoute replace to={`/flc/warehouse/acceptance`} />} />
                <Route path='warehouse/acceptance/:id' element={<RedirectRoute replace to={`/flc/warehouse/acceptance/:id`} />} />
                <Route
                    path='warehouse/acceptance/consignments/:truckId/:consignmentId'
                    element={<RedirectRoute replace to={`/flc/warehouse/acceptance/consignments/:truckId/:consignmentId`} />}
                />
                <Route path='warehouse/remainings' element={<RedirectRoute replace to={`/flc/warehouse/remainings`} />} />
                <Route
                    path='warehouse/remainings/:boxGroupId'
                    element={<RedirectRoute replace to={`/flc/warehouse/remainings/:boxGroupId`} />}
                />
                <Route
                    path='warehouse/remainings/shipment/:journalId?'
                    element={<RedirectRoute replace to={`/flc/warehouse/remainings/shipment/:journalId?`} />}
                />
                <Route path='warehouse/extraconsignments' element={<RedirectRoute replace to={`/flc/warehouse/extraconsignments`} />} />
                <Route
                    path='warehouse/extraconsignments/new'
                    element={<RedirectRoute replace to={`/flc/warehouse/extraconsignments/new`} />}
                />
                <Route
                    path='warehouse/extraconsignments/consignments/:consignmentId'
                    element={<RedirectRoute replace to={`/flc/warehouse/extraconsignments/consignments/:consignmentId`} />}
                />
                <Route path='warehouse/shipmentjournals' element={<RedirectRoute replace to={`/flc/warehouse/shipmentjournals`} />} />
                <Route
                    path='warehouse/shipmentjournals/:id'
                    element={<RedirectRoute replace to={`/flc/warehouse/shipmentjournals/:id`} />}
                />
                <Route
                    path='warehouse/shipmentjournals/:journalId/truck/new'
                    element={<RedirectRoute replace to={`/flc/warehouse/shipmentjournals/:journalId/truck/new`} />}
                />
                <Route
                    path='warehouse/shipmentjournals/:journalId/truck/:truckId'
                    element={<RedirectRoute replace to={`/flc/warehouse/shipmentjournals/:journalId/truck/:truckId`} />}
                />
                <Route
                    path='warehouse/shipmentjournals/manageoutes/:journalId/:journalNumber'
                    element={<RedirectRoute replace to={`/flc/warehouse/shipmentjournals/manageoutes/:journalId/:journalNumber`} />}
                />
                <Route
                    path='warehouse/performers/:areaType/:truckId?'
                    element={<RedirectRoute replace to={`/flc/warehouse/performers/:areaType/:truckId?`} />}
                />
                <Route
                    path='warehouse/routesheet/:journalId'
                    element={<RedirectRoute replace to={`/flc/warehouse/routesheet/:journalId`} />}
                />
                <Route path='finance/tariffs' element={<RedirectRoute replace to={`/flc/finance/tariffs`} />} />
                <Route path='finance/tariffs/new/:headerId' element={<RedirectRoute replace to={`/flc/finance/tariffs/new/:headerId`} />} />
                <Route path='finance/tariffs/:id/:isCopy?' element={<RedirectRoute replace to={`/flc/finance/tariffs/:id/:isCopy?`} />} />
                <Route path='finance/bills/:userId?' element={<RedirectRoute replace to={`/flc/finance/bills/:userId?`} />} />
                <Route path='finance/balances' element={<RedirectRoute replace to={`/flc/finance/balances`} />} />
                <Route path='finance/balances/:userId' element={<RedirectRoute replace to={`/flc/finance/balances/:userId`} />} />
                <Route path='finance/waybills' element={<RedirectRoute replace to={`/flc/finance/waybills`} />} />
                <Route path='finance/waybills/new' element={<RedirectRoute replace to={`/flc/finance/waybills/new`} />} />
                <Route path='finance/waybills/:id' element={<RedirectRoute replace to={`/flc/finance/waybills/:id`} />} />

                <Route path='documents' element={<RedirectRoute replace to={`/flc/documents`} />} />
                <Route path='settings/countries' element={<RedirectRoute replace to={`/flc/settings/countries`} />} />
                <Route path='settings/countries/new' element={<RedirectRoute replace to={`/flc/settings/countries/new`} />} />
                <Route path='settings/countries/:id' element={<RedirectRoute replace to={`/flc/settings/countries/:id`} />} />
                <Route path='settings/cities' element={<RedirectRoute replace to={`/flc/settings/cities`} />} />
                <Route path='settings/cities/new' element={<RedirectRoute replace to={`/flc/settings/cities/new`} />} />
                <Route path='settings/cities/:id' element={<RedirectRoute replace to={`/flc/settings/cities/:id`} />} />
                <Route path='settings/consignees' element={<RedirectRoute replace to={`/flc/settings/consignees`} />} />
                <Route path='settings/consignees/new' element={<RedirectRoute replace to={`/flc/settings/consignees/new`} />} />
                <Route path='settings/consignees/:id' element={<RedirectRoute replace to={`/flc/settings/consignees/:id`} />} />
                <Route path='settings/waypoints' element={<RedirectRoute replace to={`/flc/settings/waypoints`} />} />
                <Route path='settings/warehouses' element={<RedirectRoute replace to={`/flc/settings/warehouses`} />} />
                <Route path='settings/warehouses/new' element={<RedirectRoute replace to={`/flc/settings/warehouses/new`} />} />
                <Route path='settings/warehouses/:id' element={<RedirectRoute replace to={`/flc/settings/warehouses/:id`} />} />
                <Route path='settings/tags' element={<RedirectRoute replace to={`/flc/settings/tags`} />} />
                <Route path='settings/tags/new' element={<RedirectRoute replace to={`/flc/settings/tags/new`} />} />
                <Route path='settings/packagetemplates' element={<RedirectRoute replace to={`/flc/settings/packagetemplates`} />} />
                <Route path='settings/packagetemplates/new' element={<RedirectRoute replace to={`/flc/settings/packagetemplates/new`} />} />
                <Route path='settings/packagetemplates/:id' element={<RedirectRoute replace to={`/flc/settings/packagetemplates/:id`} />} />
                <Route path='settings/tradingplatforms' element={<RedirectRoute replace to={`/flc/settings/tradingplatforms`} />} />
                <Route path='settings/tradingplatforms/new' element={<RedirectRoute replace to={`/flc/settings/tradingplatforms/new`} />} />
                <Route path='settings/tradingplatforms/:id' element={<RedirectRoute replace to={`/flc/settings/tradingplatforms/:id`} />} />
                <Route path='settings/transporttariffs' element={<RedirectRoute replace to={`/flc/settings/transporttariffs`} />} />
                <Route path='settings/transporttariffs/new' element={<RedirectRoute replace to={`/flc/settings/transporttariffs/new`} />} />
                <Route
                    path='settings/transporttariffs/:id/:isCopy?'
                    element={<RedirectRoute replace to={`flc/settings/transporttariffs/:id/:isCopy?`} />}
                />
                <Route path='settings/users' element={<RedirectRoute replace to={`/flc/settings/users`} />} />
                <Route path='settings/users/new' element={<RedirectRoute replace to={`/flc/settings/users/new`} />} />
                <Route path='settings/users/:id' element={<RedirectRoute replace to={`/flc/settings/users/:id`} />} />
                <Route path='settings/counterparties' element={<RedirectRoute replace to={`/flc/settings/counterparties`} />} />
                <Route path='orders/:userId' element={<RedirectRoute replace to={`/flc/orders/:userId`} />} />
                <Route path='orders/:userId/:orderId' element={<RedirectRoute replace to={`/flc/orders/:userId/:orderId`} />} />
                <Route path='feedbacks' element={<RedirectRoute replace to={`/flc/feedbacks`} />} />
                <Route path='feedbacks/:id' element={<RedirectRoute replace to={`/flc/feedbacks/:id`} />} />
                <Route path='settings/contacts' element={<RedirectRoute replace to={`/flc/settings/contacts`} />} />
                <Route path='settings/contacts/new' element={<RedirectRoute replace to={`/flc/settings/contacts/new`} />} />
                <Route path='settings/contacts/:id' element={<RedirectRoute replace to={`/flc/settings/contacts/:id`} />} />

                <Route path='settings/accounts' element={<RedirectRoute replace to={`/flc/settings/accounts`} />} />
                <Route path='settings/accounts/new' element={<RedirectRoute replace to={`/flc/settings/accounts/new`} />} />
                <Route path='settings/accounts/:id' element={<RedirectRoute replace to={`/flc/settings/accounts/:id`} />} />

                <Route path='settings/debts' element={<RedirectRoute replace to={`/flc/settings/debts`} />} />
                <Route path='settings/debts/new' element={<RedirectRoute replace to={`/flc/settings/debts/new`} />} />
                <Route path='settings/debts/:id' element={<RedirectRoute replace to={`/flc/settings/debts/:id`} />} />

                <Route path='settings/items' element={<RedirectRoute replace to={`/flc/settings/items`} />} />
                <Route path='settings/items/new' element={<RedirectRoute replace to={`/flc/settings/items/new`} />} />
                <Route path='settings/items/:id' element={<RedirectRoute replace to={`/flc/settings/items/:id`} />} />

                <Route path='settings/companies' element={<RedirectRoute replace to={`/flc/settings/companies`} />} />
                <Route path='settings/companies/new' element={<RedirectRoute replace to={`/flc/settings/companies/new`} />} />
                <Route path='settings/companies/:id' element={<RedirectRoute replace to={`/flc/settings/companies/:id`} />} />

                <Route path='settings/cargocarriers' element={<RedirectRoute replace to={`/flc/settings/cargocarriers`} />} />
                <Route path='settings/cargocarriers/new' element={<RedirectRoute replace to={`/flc/settings/cargocarriers/new`} />} />
                <Route path='settings/cargocarriers/:id' element={<RedirectRoute replace to={`/flc/settings/cargocarriers/:id`} />} />

                <Route path='useraccount' element={<RedirectRoute replace to={`/flc/useraccount`} />} />
                <Route path='auditlogs' element={<RedirectRoute replace to={`/flc/auditlogs`} />} />

                {/* TODO: remove redirect section ------------------------------------------------------*/}

                <Route path='flc/logistics/preloading' element={<Preloading />} />
                <Route path='flc/logistics/import' element={<ImportTrucks />} />
                <Route path='flc/logistics/import/new' element={<ImportTruck />} />
                <Route path='flc/logistics/import/:id' element={<ImportTruck />} />
                <Route path='flc/logistics/import/merge/:ids' element={<MergeImportTrucks />} />
                <Route path='flc/logistics/import/consignments/:truckId' element={<ImportConsignments />} />
                <Route path='flc/logistics/import/consignments/new/:truckId' element={<ImportConsignment />} />
                <Route path='flc/logistics/import/consignments/preloading/:truckId' element={<ConsignmentPreloading />} />
                <Route
                    path='flc/logistics/import/consignments/:truckId/:consignmentId'
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageTruckConsignment) ||
                                hasPermission(userSession.permissions, Permission.ViewTruckConsignment)
                            }
                            redirectPath={'/'}
                            children={<ImportConsignment />}
                        />
                    }
                />
                <Route
                    path='flc/logistics/import/tariffs/:truckId'
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
                                hasPermission(userSession.permissions, Permission.EditTransportBill) ||
                                hasPermission(userSession.permissions, Permission.ViewTransportBill)
                            }
                            redirectPath={'/'}
                            children={<ImportBills />}
                        />
                    }
                />
                <Route path='flc/logistics/delivery' element={<DeliveryTrucks />} />
                <Route path='flc/logistics/delivery/new/:journalId' element={<DeliveryTruck />} />
                <Route path='flc/logistics/delivery/:truckId/:journalId?' element={<DeliveryTruck />} />
                <Route path='flc/warehouse/acceptance' element={<Acceptance modulePath='flc' serverFetch={serverFetch} />} />
                <Route path='flc/warehouse/acceptance/:id' element={<WarehouseTruck modulePath='flc' serverFetch={serverFetch} />} />
                <Route
                    path='flc/warehouse/acceptance/consignments/:truckId/:consignmentId'
                    element={<WarehouseConsignment serverFetch={serverFetch} />}
                />
                <Route path='flc/warehouse/remainings' element={<Remainings />} />
                <Route path='flc/warehouse/remainings/:boxGroupId' element={<Remaining />} />
                <Route path='flc/warehouse/remainings/shipment/:journalId?' element={<Remainings />} />
                <Route path='flc/warehouse/extraconsignments' element={<ExtraConsignments />} />
                <Route path='flc/warehouse/extraconsignments/new' element={<ExtraConsignment />} />
                <Route
                    path='flc/warehouse/extraconsignments/consignments/:consignmentId'
                    element={<WarehouseConsignment serverFetch={serverFetch} />}
                />
                <Route path='flc/warehouse/shipmentjournals' element={<ShipmentJournals modulePath='flc' serverFetch={serverFetch} />} />
                <Route path='flc/warehouse/shipmentjournals/:id' element={<ShipmentJournal modulePath='flc' serverFetch={serverFetch} />} />
                <Route path='flc/warehouse/shipmentjournals/:journalId/truck/new' element={<DeliveryTruck />} />
                <Route path='flc/warehouse/shipmentjournals/:journalId/truck/:truckId' element={<DeliveryTruck />} />
                <Route
                    path='flc/warehouse/shipmentjournals/manageoutes/:journalId/:journalNumber'
                    element={<ManageRoutes serverFetch={serverFetch} />}
                />
                <Route path='flc/warehouse/performers/:areaType/:truckId?' element={<Performers serverFetch={serverFetch} />} />
                <Route path='flc/warehouse/routesheet/:journalId' element={<RouteSheet serverFetch={serverFetch} />} />
                <Route
                    element={
                        <ProtectedRoute isAllowed={hasPermission(userSession.permissions, Permission.ManageTariff)} redirectPath={'/'} />
                    }
                >
                    <Route path='flc/finance/tariffs' element={<Tariffs />} />
                    <Route path='flc/finance/tariffs/new/:headerId' element={<Tariff />} />
                    <Route path='flc/finance/tariffs/:id/:isCopy?' element={<Tariff />} />
                </Route>
                <Route
                    path='flc/finance/bills/:userId?'
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
                                hasPermission(userSession.permissions, Permission.EditTransportBill) ||
                                hasPermission(userSession.permissions, Permission.ViewTransportBill) ||
                                hasPermission(userSession.permissions, Permission.SetPaid)
                            }
                            redirectPath={'/'}
                            children={<BillHeaders />}
                        />
                    }
                />
                <Route
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageBalance) ||
                                hasPermission(userSession.permissions, Permission.ViewBalance)
                            }
                            redirectPath={'/'}
                        />
                    }
                >
                    <Route path='flc/finance/balances' element={<Balances />} />
                    <Route path='flc/finance/balances/:userId' element={<BalanceDetails />} />
                </Route>
                <Route
                    element={
                        <ProtectedRoute isAllowed={hasPermission(userSession.permissions, Permission.ManageWaybill)} redirectPath={'/'} />
                    }
                >
                    <Route path='flc/finance/waybills' element={<Waybills />} />
                    <Route path='flc/finance/waybills/new' element={<Waybill />} />
                    <Route path='flc/finance/waybills/:id' element={<Waybill />} />
                </Route>

                <Route
                    path='flc/notifications/newnotification'
                    element={<NewNotification serverFetch={serverFetch} modulePath='flc' serviceName='Срезка' />}
                />
                <Route path='flc/notifications/sms' element={<SmsMessages serverFetch={serverFetch} />} />
                <Route path='flc/notifications/emails' element={<Emails serverFetch={serverFetch} />} />
                <Route path='flc/notifications/push' element={<PushMessages serverFetch={serverFetch} />} />
                <Route path='flc/notifications/telegram' element={<TelegramMessages serverFetch={serverFetch} />} />
                <Route path='flc/notifications/max' element={<MaxMessages serverFetch={serverFetch} />} />

                <Route path='flc/orders/:userId' element={<Orders />} />
                <Route path='flc/orders/:userId/:orderId' element={<Order />} />
                <Route path='flc/feedbacks' element={<Feedbacks />} />

                <Route path='flc/feedbacks/:id' element={<Feedback />} />

                <Route path='flc/documents' element={<Documents />} />

                <Route
                    element={
                        <ProtectedRoute isAllowed={hasPermission(userSession.permissions, Permission.ViewClientsActivityReport)} redirectPath={'/'} />
                    }
                >
                    <Route path='flc/reports/clientsactivityreport' element={<ClientsActivityReport modulePath='flc' serverFetch={serverFetch} />} />
                </Route>

                <Route
                    element={
                        <ProtectedRoute isAllowed={hasPermission(userSession.permissions, Permission.ViewUsersReport)} redirectPath={'/'} />
                    }
                >
                    <Route path='flc/reports/usersreport' element={<UsersReport modulePath='flc' serverFetch={serverFetch} />} />
                </Route>

                <Route
                    element={
                        <ProtectedRoute
                            isAllowed={hasPermission(userSession.permissions, Permission.ViewImportTrucksReport)}
                            redirectPath={'/'}
                        />
                    }
                >
                    <Route path='flc/reports/importreport' element={<ImportTrucksReport useDiscounts={true} serverFetch={serverFetch} />} />
                </Route>

                <Route path='flc/settings/countries' element={<Countries serverFetch={serverFetch} />} />
                <Route path='flc/settings/countries/new' element={<Country serverFetch={serverFetch} />} />
                <Route path='flc/settings/countries/:id' element={<Country serverFetch={serverFetch} />} />
                <Route path='flc/settings/cities' element={<Cities />} />
                <Route path='flc/settings/cities/new' element={<City />} />
                <Route path='flc/settings/cities/:id' element={<City />} />
                <Route path='flc/settings/consignees' element={<Consignees />} />
                <Route path='flc/settings/consignees/new' element={<Consignee />} />
                <Route path='flc/settings/consignees/:id' element={<Consignee />} />
                <Route path='flc/settings/waypoints' element={<Waypoints serverFetch={serverFetch} />} />
                <Route path='flc/settings/warehouses' element={<Warehouses />} />
                <Route path='flc/settings/warehouses/new' element={<Warehouse />} />
                <Route path='flc/settings/warehouses/:id' element={<Warehouse />} />
                <Route path='flc/settings/tags' element={<Tags />} />
                <Route path='flc/settings/tags/new' element={<Tag />} />
                <Route path='flc/settings/packagetemplates' element={<PackageTemplates />} />
                <Route path='flc/settings/packagetemplates/new' element={<PackageTemplate />} />
                <Route path='flc/settings/packagetemplates/:id' element={<PackageTemplate />} />
                <Route path='flc/settings/tradingplatforms' element={<TradingPlatforms />} />
                <Route path='flc/settings/tradingplatforms/new' element={<TradingPlatform />} />
                <Route path='flc/settings/tradingplatforms/:id' element={<TradingPlatform />} />
                <Route path='flc/settings/transporttariffs' element={<TransportTariffs />} />
                <Route path='flc/settings/transporttariffs/new' element={<TransportTariff />} />
                <Route path='flc/settings/transporttariffs/:id/:isCopy?' element={<TransportTariff />} />
                <Route path='flc/settings/users' element={<Users />} />
                <Route path='flc/settings/users/new' element={<User />} />
                <Route path='flc/settings/users/:id' element={<User />} />
                <Route path='flc/settings/counterparties' element={<Counterparties />} />
                <Route path='flc/settings/contacts' element={<Contacts />} />
                <Route path='flc/settings/contacts/new' element={<Contact />} />
                <Route path='flc/settings/contacts/:id' element={<Contact />} />
                <Route path='flc/settings/accounts' element={<Accounts />} />
                <Route path='flc/settings/accounts/new' element={<Account />} />
                <Route path='flc/settings/accounts/:id' element={<Account />} />
                <Route path='flc/settings/debts' element={<Debts />} />
                <Route path='flc/settings/debts/new' element={<Debt />} />
                <Route path='flc/settings/debts/:id' element={<Debt />} />
                <Route path='flc/settings/items' element={<Items serverFetch={serverFetch} />} />
                <Route path='flc/settings/items/new' element={<Item serverFetch={serverFetch} />} />
                <Route path='flc/settings/items/:id' element={<Item serverFetch={serverFetch} />} />
                <Route path='flc/settings/companies' element={<Companies serverFetch={serverFetch} />} />
                <Route path='flc/settings/companies/new' element={<Company serverFetch={serverFetch} />} />
                <Route path='flc/settings/companies/:id' element={<Company serverFetch={serverFetch} />} />
                <Route path='flc/settings/cargocarriers' element={<CargoCarriers />} />
                <Route path='flc/settings/cargocarriers/new' element={<CargoCarrier />} />
                <Route path='flc/settings/cargocarriers/:id' element={<CargoCarrier />} />
                <Route path='flc/useraccount' element={<UserAccount />} />
                <Route path='flc/auditlogs' element={<AuditLogs />} />
            </Route>
        </Routes>
    );
};

export default AppRoutes;
